import { createAction, props } from '@ngrx/store';
import { BillingEvent, Contact, Matter, TimelineEvent } from '../../shared/models';

// Load Contacts
export const loadContacts = createAction(
  '[CRM] Load Contacts',
  props<{ skip?: number; limit?: number; search?: string; contactType?: string }>()
);
export const loadContactsSuccess = createAction(
  '[CRM] Load Contacts Success',
  props<{ contacts: Contact[]; total: number }>()
);
export const loadContactsFailure = createAction(
  '[CRM] Load Contacts Failure',
  props<{ error: string }>()
);

// Load Contact Detail
export const loadContactDetail = createAction(
  '[CRM] Load Contact Detail',
  props<{ contactId: string }>()
);
export const loadContactDetailSuccess = createAction(
  '[CRM] Load Contact Detail Success',
  props<{ contact: Contact }>()
);
export const loadContactDetailFailure = createAction(
  '[CRM] Load Contact Detail Failure',
  props<{ error: string }>()
);

// Create Contact
export const createContact = createAction(
  '[CRM] Create Contact',
  props<{ contact: Partial<Contact> }>()
);
export const createContactSuccess = createAction(
  '[CRM] Create Contact Success',
  props<{ contact: Contact }>()
);
export const createContactFailure = createAction(
  '[CRM] Create Contact Failure',
  props<{ error: string }>()
);

// Update Contact
export const updateContact = createAction(
  '[CRM] Update Contact',
  props<{ contactId: string; contact: Partial<Contact> }>()
);
export const updateContactOptimistic = createAction(
  '[CRM] Update Contact Optimistic',
  props<{ contactId: string; contact: Partial<Contact> }>()
);
export const updateContactSuccess = createAction(
  '[CRM] Update Contact Success',
  props<{ contact: Contact; contactId?: string }>()
);
export const updateContactFailure = createAction(
  '[CRM] Update Contact Failure',
  props<{ error: string; contactId?: string }>()
);

// Load Matters
export const loadMatters = createAction(
  '[CRM] Load Matters',
  props<{ skip?: number; limit?: number; status?: string; contactId?: string }>()
);
export const loadMattersSuccess = createAction(
  '[CRM] Load Matters Success',
  props<{ matters: Matter[]; total: number }>()
);
export const loadMattersFailure = createAction(
  '[CRM] Load Matters Failure',
  props<{ error: string }>()
);
export const clearMatters = createAction('[CRM] Clear Matters');

// Load Matter Detail
export const loadMatterDetail = createAction(
  '[CRM] Load Matter Detail',
  props<{ matterId: string }>()
);
export const loadMatterDetailSuccess = createAction(
  '[CRM] Load Matter Detail Success',
  props<{ matter: Matter }>()
);
export const loadMatterDetailFailure = createAction(
  '[CRM] Load Matter Detail Failure',
  props<{ error: string }>()
);
export const clearCurrentMatter = createAction('[CRM] Clear Current Matter');

// Create Matter
export const createMatter = createAction(
  '[CRM] Create Matter',
  props<{ matter: Partial<Matter> }>()
);
export const createMatterSuccess = createAction(
  '[CRM] Create Matter Success',
  props<{ matter: Matter }>()
);
export const createMatterFailure = createAction(
  '[CRM] Create Matter Failure',
  props<{ error: string }>()
);

// Update Matter
export const updateMatter = createAction(
  '[CRM] Update Matter',
  props<{ matterId: string; matter: Partial<Matter> }>()
);
export const updateMatterSuccess = createAction(
  '[CRM] Update Matter Success',
  props<{ matter: Matter; matterId?: string }>()
);
export const updateMatterFailure = createAction(
  '[CRM] Update Matter Failure',
  props<{ error: string; matterId?: string }>()
);

// Timeline
export const loadMatterTimeline = createAction(
  '[CRM] Load Matter Timeline',
  props<{ matterId: string; cursor?: string; order?: 'asc' | 'desc'; limit?: number }>()
);
export const loadMatterTimelineSuccess = createAction(
  '[CRM] Load Matter Timeline Success',
  props<{ timeline: TimelineEvent[]; nextCursor: string | null }>()
);
export const loadMatterTimelineFailure = createAction(
  '[CRM] Load Matter Timeline Failure',
  props<{ error: string }>()
);

// Billing Events
export const loadBillingEvents = createAction(
  '[CRM] Load Billing Events',
  props<{ matterId: string }>()
);
export const loadBillingEventsSuccess = createAction(
  '[CRM] Load Billing Events Success',
  props<{ events: BillingEvent[]; totalAmount: number }>()
);
export const loadBillingEventsFailure = createAction(
  '[CRM] Load Billing Events Failure',
  props<{ error: string }>()
);

export const addBillingEvent = createAction(
  '[CRM] Add Billing Event',
  props<{
    matterId: string;
    request: {
      amount: number;
      currency: string;
      eventDate: string;
      description: string;
      type?: string;
    };
    optimisticEvent: BillingEvent;
  }>()
);
export const addBillingEventSuccess = createAction(
  '[CRM] Add Billing Event Success',
  props<{ matterId: string; event: BillingEvent }>()
);
export const addBillingEventFailure = createAction(
  '[CRM] Add Billing Event Failure',
  props<{ error: string }>()
);

// Create Intake (contact + matter in one flow)
export const createIntake = createAction(
  '[CRM] Create Intake',
  props<{
    contact: Partial<Contact>;
    matter: Partial<Matter>;
  }>()
);
export const createIntakeSuccess = createAction(
  '[CRM] Create Intake Success',
  props<{ contact: Contact; matter: Matter }>()
);
export const createIntakeFailure = createAction(
  '[CRM] Create Intake Failure',
  props<{ error: string }>()
);

export const clearCrmError = createAction('[CRM] Clear Error');

// Documents
export const loadMatterDocuments = createAction(
  '[CRM] Load Matter Documents',
  props<{ matterId: string }>()
);
export const loadMatterDocumentsSuccess = createAction(
  '[CRM] Load Matter Documents Success',
  props<{ documents: any[] }>()
);
export const loadMatterDocumentsFailure = createAction(
  '[CRM] Load Matter Documents Failure',
  props<{ error: string }>()
);

export const uploadMatterDocument = createAction(
  '[CRM] Upload Matter Document',
  props<{ matterId: string; formData: FormData; filename: string }>()
);
export const uploadMatterDocumentSuccess = createAction(
  '[CRM] Upload Matter Document Success',
  props<{ document: any }>()
);
export const uploadMatterDocumentFailure = createAction(
  '[CRM] Upload Matter Document Failure',
  props<{ error: string }>()
);
export const clearUploadProgress = createAction('[CRM] Clear Upload Progress');

export const deleteMatterDocument = createAction(
  '[CRM] Delete Matter Document',
  props<{ documentId: string; filename: string; matterId: string }>()
);
export const deleteMatterDocumentSuccess = createAction(
  '[CRM] Delete Matter Document Success',
  props<{ documentId: string; matterId: string }>()
);
export const deleteMatterDocumentFailure = createAction(
  '[CRM] Delete Matter Document Failure',
  props<{ error: string }>()
);

// Timeline Event mutations
export const createMatterTimelineEvent = createAction(
  '[CRM] Create Matter Timeline Event',
  props<{ matterId: string; event: { eventType: string; title: string; eventDate: string; location?: string; description?: string; status?: string } }>()
);
export const createMatterTimelineEventSuccess = createAction(
  '[CRM] Create Matter Timeline Event Success',
  props<{ matterId: string }>()
);
export const createMatterTimelineEventFailure = createAction(
  '[CRM] Create Matter Timeline Event Failure',
  props<{ error: string }>()
);

export const updateMatterTimelineEvent = createAction(
  '[CRM] Update Matter Timeline Event',
  props<{ matterId: string; eventId: string; event: { eventType?: string; title?: string; eventDate?: string; location?: string; description?: string; status?: string } }>()
);
export const updateMatterTimelineEventSuccess = createAction(
  '[CRM] Update Matter Timeline Event Success',
  props<{ matterId: string }>()
);
export const updateMatterTimelineEventFailure = createAction(
  '[CRM] Update Matter Timeline Event Failure',
  props<{ error: string }>()
);

export const archiveMatterTimelineEvent = createAction(
  '[CRM] Archive Matter Timeline Event',
  props<{ matterId: string; eventId: string }>()
);
export const archiveMatterTimelineEventSuccess = createAction(
  '[CRM] Archive Matter Timeline Event Success',
  props<{ matterId: string }>()
);
export const archiveMatterTimelineEventFailure = createAction(
  '[CRM] Archive Matter Timeline Event Failure',
  props<{ error: string }>()
);

export const deleteMatterTimelineEvent = createAction(
  '[CRM] Delete Matter Timeline Event',
  props<{ matterId: string; eventId: string }>()
);
export const deleteMatterTimelineEventSuccess = createAction(
  '[CRM] Delete Matter Timeline Event Success',
  props<{ matterId: string }>()
);
export const deleteMatterTimelineEventFailure = createAction(
  '[CRM] Delete Matter Timeline Event Failure',
  props<{ error: string }>()
);
